/**
 * Envío AP-4 · guardar la imagen editada (PNG nuevo en R2 + Galería) y
 * «Llevar al Canvas» (nodo con la imagen conectado a Banana 2 Pro).
 * El original nunca se modifica.
 */
import { editedFileName, encodeOutput, normalizeParams, renderEdit } from '../../utils/imageEdit';
import { uploadToR2 } from '../../utils/uploadToR2';
import { useWorkflowStore } from '../../store/workflowStore';

/** Sube la versión nueva. `background`: color para aplanar (pista base: negro). */
export async function saveEditedImage({ img, params, label, background = null, onProgress }) {
  const p = normalizeParams(params);
  const canvas = renderEdit(img, p);
  // AP-14 · tamaño, formato y peso de la salida (por defecto: PNG del mismo tamaño)
  const enc = await encodeOutput(canvas, p.output, { background });
  const kind = isOnlyOutput(p) ? 'adaptada' : (p.marks.length > 0 && isOnlyMarks(p) ? 'anotada' : 'editada');
  const name = editedFileName(label, kind, enc.ext);
  const file = new File([enc.blob], name, { type: enc.mime });
  const out = await uploadToR2(file, { onProgress, origin: 'image_edit' });
  return { url: out.url, assetId: out.assetId, name, width: enc.width, height: enc.height, bytes: enc.blob.size, format: enc.format };
}

/** Estimación para la pestaña «Tamaño» (no sube nada). */
export async function estimateOutput({ img, params, background = null }) {
  const p = normalizeParams(params);
  const enc = await encodeOutput(renderEdit(img, p), p.output, { background });
  return { width: enc.width, height: enc.height, bytes: enc.blob.size, format: enc.format, fits: enc.fits };
}

function isOnlyMarks(p) {
  const d = normalizeParams({});
  const strip = (x) => JSON.stringify({ ...x, marks: [], output: d.output, shape: { ...x.shape, points: null } });
  return strip(p) === strip(d);
}

function isOnlyOutput(p) {
  const d = normalizeParams({});
  const strip = (x) => JSON.stringify({ ...x, output: d.output, shape: { ...x.shape, points: null } });
  return strip(p) === strip(d) && outputChanged(p);
}
const outputChanged = (p) => JSON.stringify(p.output) !== JSON.stringify(normalizeParams({}).output);

export const defaultCanvasPrompt = (params) => (normalizeParams(params).marks.length
  ? 'Aplicá los cambios marcados en la imagen. No incluyas las marcas en el resultado.'
  : 'Usá esta imagen como referencia.');

/**
 * Crea en el Canvas un nodo «Upload Image» con la imagen nueva, conectado a un
 * Banana 2 Pro con el pedido listo. Devuelve los ids o null si no hay Canvas.
 */
export function sendToCanvas({ url, name, prompt, near = null }) {
  const st = useWorkflowStore.getState();
  if (!st?.addNode) return null;
  const nodes = st.nodes || [];
  const maxX = nodes.reduce((m, n) => Math.max(m, n.position?.x ?? 0), 0);
  const base = near ? { x: near.x + 360, y: near.y } : { x: (nodes.length ? maxX + 380 : 120), y: 160 };
  const imageId = st.addNode('uploadImage', base, {
    outputUrl: url, hasOutput: true, uploaded: true, type: 'uploaded', fileName: name,
  });
  const bananaId = st.addNode('banana2pro', { x: base.x + 360, y: base.y }, { prompt: prompt || '' });
  st.onConnect({ source: imageId, target: bananaId, sourceHandle: 'output', targetHandle: 'multiInput' });
  return { imageId, bananaId };
}
